// src/components/CreateVariableForm.js

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Box
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useAuth } from "../util/auth";
import { createVariable, upsertTagsForEntity } from "../util/db";

function CreateVariableForm() {
  const auth = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formAlert, setFormAlert] = useState(null);

  const { register, handleSubmit, errors, reset } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setFormAlert(null);

    try {
      if (auth.user.role !== "in_good_standing") {
        throw new Error("You must be in good standing to add variables.");
      }

      // 1) Create variable
      const [newVar] = await createVariable({
        owner: auth.user.uid,
        title: data.title || "",
        content: data.content || "",
        shorthand: data.shorthand
      });

      // 2) Parse up to 50 tags
      const tags = parseCommaSeparated(data.tags);
      if (tags.length > 50) {
        throw new Error("Cannot exceed 50 tags");
      }

      // 3) Upsert and attach
      await upsertTagsForEntity({
        entityType: "variable",
        entityId: newVar.id,
        tags,
      });

      reset();
    } catch (error) {
      setFormAlert(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {formAlert && (
        <Box mb={2}>
          <Alert severity="error">{formAlert}</Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Shorthand (unique)"
            name="shorthand"
            variant="outlined"
            size="small"
            fullWidth
            error={!!errors.shorthand}
            helperText={errors.shorthand && errors.shorthand.message}
            inputRef={register({
              required: "Please provide a shorthand",
            })}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Title (optional)"
            name="title"
            variant="outlined"
            size="small"
            fullWidth
            inputRef={register}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Content (optional)"
            name="content"
            multiline
            rows={4}
            variant="outlined"
            size="small"
            fullWidth
            inputRef={register}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Tags (comma-separated)"
            name="tags"
            variant="outlined"
            size="small"
            fullWidth
            inputRef={register}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={22} /> : "Create Variable"}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

function parseCommaSeparated(text = "") {
  return text
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

export default CreateVariableForm;
